/**
 * SIL-1647: bench agent <-> API wire contract.
 *
 * Every shape the Rust agent (apps/bench-agent, serde camelCase) and the API
 * exchange is defined here and nowhere else. The fixtures in
 * apps/bench-agent/fixtures/contract/ are parsed by both sides; see
 * __tests__/contract-fixtures.test.ts.
 *
 * Strings that originate on the device (serials, models, vendors) are shown to
 * a portal user before they approve a pairing, so they are printable ASCII
 * only: no control characters and no bidirectional overrides that could make
 * one serial look like another.
 */
import { z } from 'zod';
import { isValidLabelPrinterAddr } from './printer-addr.js';

const asciiText = (max: number) =>
  z
    .string()
    .max(max)
    .regex(/^[\x20-\x7e]*$/);

export const benchDeviceKindSchema = z.enum(['chassis', 'gpu', 'nvme', 'dimm', 'cpu', 'psu']);

export const benchDeviceSchema = z.object({
  deviceIndex: z.number().int().nonnegative(),
  // NVMe: `${serial}|${model}|${eui64OrNguid}`; others: `${kind}:${serial ?? pciAddress ?? index}`
  deviceKey: asciiText(256).min(1),
  kind: benchDeviceKindSchema,
  parentDeviceIndex: z.number().int().nonnegative().nullable(),
  pciId: z
    .string()
    .regex(/^[0-9a-f]{4}:[0-9a-f]{4}:[0-9a-f]{4}:[0-9a-f]{4}$/)
    .nullable(),
  // SIL-2250: PCI class code, 6 lowercase hex digits (sysfs `class` 0x030200 -> "030200"). Optional and nullable:
  // expand-only, an older agent omits it and an older zod strips it.
  pciClass: z
    .string()
    .regex(/^[0-9a-f]{6}$/)
    .nullable()
    .optional(),
  pciAddress: asciiText(32).nullable(),
  pcieGen: z.number().int().nullable(),
  pcieWidth: z.number().int().nullable(),
  vendor: asciiText(128).nullable(),
  model: asciiText(128).nullable(),
  serial: asciiText(128).nullable(),
  firmware: asciiText(64).nullable(),
  nvme: z
    .object({
      eui64: asciiText(64).nullable(),
      nguid: asciiText(64).nullable(),
      ieeeOui: asciiText(64).nullable(),
      percentageUsed: z.number().int().min(0).max(255).nullable(),
      mediaErrors: z.number().int().nonnegative().nullable(),
    })
    .nullable(),
  readErrors: z.array(z.object({ field: asciiText(64), reason: asciiText(256) })),
});

export const benchChassisSchema = z.object({
  sysVendor: asciiText(128).nullable(),
  productName: asciiText(128).nullable(),
  productSerial: asciiText(128).nullable(),
  chassisSerial: asciiText(128).nullable(),
  boardSerial: asciiText(128).nullable(),
  // Keys and values are device-originated and shown to the approver: printable
  // ASCII only, and at most 64 entries so one pair/start cannot bloat device_summary.
  fru: z
    .record(asciiText(128), asciiText(128))
    .refine((r) => Object.keys(r).length <= 64, { message: 'fru has more than 64 keys' })
    .nullable(),
  /**
   * SIL-1718 (E-32, spec 7.4): the platform Secure Boot state, read only by the agent from efivarfs (it never writes it).
   * Additive and optional: an older agent omits it. NOT part of the `discovery_changed` comparison at commit (a tool may
   * change it legitimately); the value frozen at pairing is what the `secure_boot_recorded` gate reads.
   */
  secureBoot: z.enum(['enabled', 'disabled', 'setup_mode', 'unsupported', 'not_uefi', 'unreadable']).optional(),
});

/** The discovery shape without the duplicate-key rule: for reading stored discoveries, which must still render. */
export const benchDiscoveryShapeSchema = z.object({
  schemaVersion: z.literal(1),
  chassis: benchChassisSchema,
  devices: z.array(benchDeviceSchema).max(512),
});

export const benchDiscoverySchema = benchDiscoveryShapeSchema.superRefine((d, ctx) => {
  // A shared key would let one wipe authorization name several drives.
  const seen = new Set<string>();
  // SIL-2082: grading and the profile requirement are keyed by deviceIndex, so two devices sharing one would overwrite
  // each other's requirement.
  const seenIndex = new Set<number>();
  d.devices.forEach((dev, i) => {
    if (seen.has(dev.deviceKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['devices', i, 'deviceKey'],
        message: 'duplicate deviceKey',
      });
    }
    seen.add(dev.deviceKey);
    if (seenIndex.has(dev.deviceIndex)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['devices', i, 'deviceIndex'],
        message: 'duplicate deviceIndex',
      });
    }
    seenIndex.add(dev.deviceIndex);
  });
});

export const benchPairStartResponseSchema = z.object({
  deviceCode: z.string(),
  userCode: z.string(),
  verificationUri: z.string().url(),
  verificationUriComplete: z.string().url(),
  interval: z.number().int(),
  expiresIn: z.number().int(),
});

export const benchPairPollRequestSchema = z.object({
  deviceCode: z.string(),
  // E-23a (SIL-2002): optional, recorded on segment 0 when the approved poll lands.
  agentVersion: asciiText(64).min(1).optional(),
  imageId: asciiText(128).min(1).optional(),
});

export const benchAttestationLevelSchema = z.enum(['registered_bench', 'self_reported']);

/**
 * SIL-1671 E-09: multi-environment runs. Where a stage runs and who can attest
 * to it. Everything here is optional on the wire so a deployed API and an
 * older agent keep working; an absent `environment` means `linux`.
 */
export const benchEnvironmentSchema = z.enum(['linux', 'bmc', 'uefi', 'winpe']);
/** The environment of an agent that can present a resume token. */
export const benchAgentEnvironmentSchema = z.enum(['linux', 'winpe']);
/** A tokenless foreign image the machine visits before the returning agent shows up. */
export const benchForeignVisitSchema = z.literal('uefi');
/** How a `uefi` stage's result comes back (spec 7.2). */
export const benchCollectSchema = z.enum(['bmc_log', 'vendor_export', 'operator']);
/**
 * SIL-2133 R2: a boot tool the server resolves to a pinned image. The agent names a TOOL, never an image: the server
 * picks the bytes (bench_boot_tool_resolve), so "the agent never chooses a sha" is a contract property, not a convention.
 * The closed list is the same one bench_boot_tool_file_pattern (migration 1163) holds.
 */
export const benchBootToolIdSchema = z.enum(['memtest86plus']);
export type BenchBootToolId = z.infer<typeof benchBootToolIdSchema>;
/**
 * SIL-2246 D1: the plan step kind a profile stage is tagged with (`bench_step_kind.kind`, migration 1187). The shape is
 * checked here; WHICH kinds exist is the database catalog's call (owner-written, seeded), so a kind added later does not
 * break an older reader's profile parse. The tagging trigger on bench_matrix_profile (INSERT) enforces that the kind
 * exists, is active, fits the profile's device kind, and agrees with the stage's `destructive` flag and evidence class.
 */
export const benchStepKindSchema = z.string().regex(/^[a-z_]{2,32}$/);
export type BenchStepKind = z.infer<typeof benchStepKindSchema>;
/**
 * Who observed the result. Assigned by the API from the pinned profile: it is
 * carried by profile stages (server -> agent) and stored rows, and is
 * deliberately absent from every request body an agent or portal user sends.
 * Anything other than `agent_observed` is negative-only for grading.
 */
export const benchEvidenceClassSchema = z.enum([
  'agent_observed',
  'bmc_reported',
  'operator_attested',
]);

export type BenchEnvironment = z.infer<typeof benchEnvironmentSchema>;
export type BenchAgentEnvironment = z.infer<typeof benchAgentEnvironmentSchema>;
export type BenchCollect = z.infer<typeof benchCollectSchema>;
export type BenchEvidenceClass = z.infer<typeof benchEvidenceClassSchema>;

/** Stage environments each agent kind executes (spec 3.2). `uefi` has no agent. */
export const BENCH_AGENT_CAPABILITIES: Record<BenchAgentEnvironment, readonly BenchEnvironment[]> =
  {
    linux: ['linux', 'bmc'],
    winpe: ['winpe'],
  };

export function benchAgentCanRun(
  agent: BenchAgentEnvironment,
  stage: BenchEnvironment | undefined,
): boolean {
  return BENCH_AGENT_CAPABILITIES[agent].includes(stage ?? 'linux');
}

export const benchPairStartRequestSchema = z.object({
  discovery: benchDiscoverySchema,
  primaryMac: z.string().regex(/^([0-9a-f]{2}:){5}[0-9a-f]{2}$/),
  /**
   * Environment of the booting agent, required (SIL-1873 expand: every agent
   * sends it; SIL-2081 contract: a body without it is a 422, never read as
   * `linux`). The API refuses a non-attach approval when this is `winpe` (spec
   * 8.1, T20): a winpe agent can only continue an existing run.
   */
  environment: benchAgentEnvironmentSchema,
  /**
   * SIL-2246 R1, additive: what the booting agent can do beyond the baseline. `planAssignments` = it runs the server-resolved
   * `assignments` of GET /profiles instead of its built-in device-to-profile rules. Closed enum, at most 4. The API stores it
   * in `bench_pairing.device_summary` and refuses plan approval for an image that does not declare it. A UX and fail-closed
   * gate, not proof (the agent is trusted at pairing like all discovery). An older agent omits it and an older zod strips it.
   */
  capabilities: z.array(z.enum(['planAssignments'])).max(4).optional(),
});

/** Opaque URL-safe secret (32 random bytes encode to 43 chars). */
const opaqueToken = z.string().regex(/^[A-Za-z0-9_-]{32,128}$/);
/**
 * Stable name of one stage inside a pinned plan; opaque to the agent. The
 * format is `${profileId}:${stageIndex}` (a profile-stage reference, not a
 * per-device completion: see `benchCompletedStageSchema`).
 */
export const benchStageKeySchema = asciiText(256).min(1);
const MAX_SEGMENTS = 8;
const MAX_STAGE_KEYS = 512;
const MAX_COMPLETED_STAGES = 4096;
/**
 * SIL-2023: the most bench_run_stage rows ONE run may hold (every class, every segment, superseded ones included).
 * Rows per segment = matched devices x stage keys per device, so the realistic bound is devices x keys x segments:
 * a 102-bay JBOD (102 drives) x 5 stages x MAX_SEGMENTS (8) is ~4080 before skipped and operator rows, and a plan
 * may name up to MAX_STAGE_KEYS keys. 65536 leaves ~16x headroom over that case and still bounds the payloads
 * (priorSeqs is 65536 ints at worst, ~0.5 MB). It is NOT tied to MAX_COMPLETED_STAGES (distinct completed keys have
 * their own cap, with its own BenchAssemblyError path). One constant, four readers: the stage POST refuses a new
 * row past it (under the run lock), `priorSeqs` is capped by it, and `stageSeqs` on commit and on handoff (SIL-1924)
 * are capped by it. Over-cap therefore fails at write time (422 run_stage_limit) and never leaves a run stuck at
 * resume. db/schema/1140 bench_run_segment_claim.seqs repeats the 65536 in a CHECK; they move together.
 */
export const MAX_RUN_STAGE_ROWS = 65536;

/**
 * One visit of the run. Step i is segment i: step 0 is the paired Linux boot,
 * so a foreign visit (`uefi`) or a `winpe` agent can only appear from step 1.
 */
const benchPlanStepShape = {
  /** Environment of the agent that presents the resume token for this visit. */
  environment: benchAgentEnvironmentSchema,
  foreignVisit: benchForeignVisitSchema.optional(),
  /**
   * Boot image of this visit (the foreign image when `foreignVisit` is set,
   * else the winpe image). The API needs it per step to refuse `internal_only`
   * images at plan time (spec 7.4). Required exactly when the step boots a
   * non-default image. SIL-2133 R2: on a REQUEST a foreign step carries `toolId` and never `imageId` (the server writes
   * `imageId` itself); only the STORED plan holds both.
   */
  imageId: asciiText(128).min(1).optional(),
  /** SIL-2133 R2: the boot tool of a foreign step (the server resolves it to the pinned image). */
  toolId: benchBootToolIdSchema.optional(),
  stageKeys: z.array(benchStageKeySchema).min(1).max(MAX_STAGE_KEYS),
};

type PlanStepShape = z.infer<z.ZodObject<typeof benchPlanStepShape>>;

/** `toolId` is a foreign-visit field: a winpe step or a plain linux step never carries one. */
function refineToolIdPlacement(step: PlanStepShape, ctx: z.RefinementCtx): void {
  if (step.toolId !== undefined && step.foreignVisit === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['toolId'], message: 'toolId is only allowed on a foreignVisit step' });
  }
}

/** The STORED step (read side): a foreign or winpe step holds an `imageId`; a foreign step may also hold the `toolId` it came from. */
export const benchPlanStepSchema = z.object(benchPlanStepShape).superRefine((step, ctx) => {
  const needsImage = step.foreignVisit !== undefined || step.environment === 'winpe';
  if (needsImage && step.imageId === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['imageId'],
      message: 'imageId is required for a foreignVisit or winpe step',
    });
  }
  if (!needsImage && step.imageId !== undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['imageId'],
      message: 'imageId is only allowed on a foreignVisit or winpe step',
    });
  }
  refineToolIdPlacement(step, ctx);
});

/**
 * SIL-2133 R2: the REQUEST step. A foreign step must name a `toolId` and must NOT name an `imageId` (the server resolves the
 * tool and writes the image into the stored step). A winpe step keeps `imageId` required (the API still refuses winpe).
 */
export const benchPlanRequestStepSchema = z.object(benchPlanStepShape).superRefine((step, ctx) => {
  refineToolIdPlacement(step, ctx);
  if (step.foreignVisit !== undefined) {
    if (step.toolId === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['toolId'], message: 'a foreignVisit step must name a toolId' });
    }
    if (step.imageId !== undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['imageId'], message: 'a foreignVisit step must name a toolId, not an image' });
    }
    return;
  }
  if (step.environment === 'winpe' && step.imageId === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['imageId'], message: 'imageId is required for a foreignVisit or winpe step' });
  }
  if (step.environment !== 'winpe' && step.imageId !== undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['imageId'], message: 'imageId is only allowed on a foreignVisit or winpe step' });
  }
});

function planWith<S extends z.ZodTypeAny>(stepSchema: S) {
  return z
    .object({
      profilePins: z
        .array(
          z.object({
            profileId: asciiText(128).min(1),
            version: z.number().int().positive().max(2147483647),
          }),
        )
        .min(1)
        .max(64),
      steps: z.array(stepSchema).min(1).max(MAX_SEGMENTS),
    })
    .superRefine((plan, ctx) => {
      const seen = new Set<string>();
      plan.profilePins.forEach((p, i) => {
        if (seen.has(p.profileId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['profilePins', i, 'profileId'],
            message: 'duplicate profileId',
          });
        }
        seen.add(p.profileId);
      });
      const steps = plan.steps as PlanStepShape[];
      const first = steps[0];
      if (first && (first.environment !== 'linux' || first.foreignVisit !== undefined)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['steps', 0],
          message: 'step 0 is the paired linux boot: linux, no foreignVisit',
        });
      }
      const last = steps[steps.length - 1];
      if (last && last.environment !== 'linux') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['steps', steps.length - 1, 'environment'],
          message: 'the last step must be linux (the run commits from linux)',
        });
      }
    });
}

/** Run-level and write-once. Carries no evidence class: that comes from the pinned profile. The STORED shape. */
export const benchPlanSchema = planWith(benchPlanStepSchema);

/** SIL-2133 R2: what the agent POSTs. Same as the stored plan except a foreign step names a `toolId`, never an `imageId`. */
export const benchPlanRequestSchema = planWith(benchPlanRequestStepSchema);
export const benchPlanResponseSchema = z.object({ plan: benchPlanSchema });

/** The per-device completion of one profile stage (spec 5.5 natural key, minus segment). */
export const benchCompletedStageSchema = z
  .object({
    deviceKey: asciiText(256).min(1),
    profileId: asciiText(128).min(1),
    stageIndex: z.number().int().min(0).max(2147483647),
    environment: benchEnvironmentSchema,
    /**
     * SIL-2023, additive: the key's status, WORST-OF over its listed rows (the rule of grade.ts): fail = timeout >
     * pass > skipped (fail when both fail and timeout exist). A later passing re-run does not hide an earlier failure.
     * Absent from an older API.
     */
    status: z.enum(['pass', 'fail', 'timeout', 'skipped']).optional(),
  })
  .strict();

export const benchRunStateSchema = z.object({
  runId: z.string().uuid(),
  status: z.enum(['running', 'committed', 'abandoned']),
  wipeAuthorizedDeviceKeys: z.array(z.string()),
  // Latest action for the device is 'revoke': the agent skips its destructive
  // stage (errorReason 'wipe_declined') instead of waiting forever.
  wipeDeclinedDeviceKeys: z.array(z.string()),
});

/**
 * SIL-1714 (E-28): BMC ephemeral-account contract, shared by the API route and the agent (SIL-1713). The API is the
 * single source of truth: the agent mirrors these shapes through fixtures.
 *
 * `slot` is the IPMI user slot id (1..99, no leading zero) and is the subject of all three events of one account
 * cycle. There is no account name, password, free text or generic detail object anywhere on this wire.
 * `tlsPinSha256` is the sha-256 of the BMC's leaf certificate DER as 64 lowercase hex digits (the definition of
 * packages/redfish-parsers tls-pin after its normalisation). `accountNameSha256` is sha256(runId ':' accountName) as
 * 64 lowercase hex digits, so segment B can confirm the name at a slot before deleting without the server holding it.
 */
export const benchBmcSlotSchema = z.string().regex(/^[1-9][0-9]?$/);
export const benchBmcHexSchema = z.string().regex(/^[0-9a-f]{64}$/);
/** Every reason closes the record. A slot that now holds a foreign account is NOT a reason: the record stays open. */
export const benchBmcRemovedReasonSchema = z.enum(['verified', 'ipmi_verified_only', 'never_created', 'already_absent']);
export type BenchBmcRemovedReason = z.infer<typeof benchBmcRemovedReasonSchema>;

const bmcSegmentIdx = z.number().int().nonnegative().max(MAX_SEGMENTS - 1);

export const benchBmcEventRequestSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('intent'),
    slot: benchBmcSlotSchema,
    segmentIdx: bmcSegmentIdx,
    /** Required: taken by a TLS probe BEFORE any account or credential exists. First write per run wins. */
    tlsPinSha256: benchBmcHexSchema,
    accountNameSha256: benchBmcHexSchema,
  }).strict(),
  z.object({
    kind: z.literal('created'),
    slot: benchBmcSlotSchema,
    segmentIdx: bmcSegmentIdx,
    /** Optional; when present it must equal the run's stored pin. */
    tlsPinSha256: benchBmcHexSchema.optional(),
  }).strict(),
  z.object({
    kind: z.literal('removed'),
    slot: benchBmcSlotSchema,
    segmentIdx: bmcSegmentIdx,
    reason: benchBmcRemovedReasonSchema,
  }).strict(),
]);
export type BenchBmcEventRequest = z.infer<typeof benchBmcEventRequestSchema>;

export const benchBmcEventResponseSchema = z.object({
  /** The stored event id as a string (a bigint). On a replay it is the id of the event already stored. */
  eventId: z.string().regex(/^[0-9]{1,19}$/),
  kind: z.enum(['intent', 'created', 'removed']),
  slot: benchBmcSlotSchema,
  replay: z.boolean(),
  /** The run's stored pin after this call (first write wins). Authoritative over anything the agent holds in RAM. */
  tlsPinSha256: benchBmcHexSchema.nullable(),
});
export type BenchBmcEventResponse = z.infer<typeof benchBmcEventResponseSchema>;

/**
 * SIL-2113: the agent's report on the one-time BMC boot override it sets at a handoff boundary
 * (POST /bench/runs/:id/bmc-override, migration 1155 bench_record_bmc_override). `reason`: attempted (posted BEFORE the Set
 * cycle opens its account), set, fallback (this cycle wrote nothing), residue (a write was attempted and Disabled could not be
 * proven), cleared (Disabled read back). `code` is the agent's static error string (never BMC text), required on fallback and
 * residue and absent otherwise. There is no free text, account, slot, pin or address on this wire. `.strict()`.
 */
export const benchBmcOverrideReasonSchema = z.enum(['attempted', 'set', 'fallback', 'residue', 'cleared']);
export type BenchBmcOverrideReason = z.infer<typeof benchBmcOverrideReasonSchema>;
export const benchBmcOverrideCodeSchema = z.string().regex(/^[a-z0-9_]{1,64}$/);

export const benchBmcOverrideRequestSchema = z
  .object({
    segmentIdx: bmcSegmentIdx,
    reason: benchBmcOverrideReasonSchema,
    code: benchBmcOverrideCodeSchema.optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    const needsCode = v.reason === 'fallback' || v.reason === 'residue';
    if (needsCode && v.code === undefined) ctx.addIssue({ code: 'custom', path: ['code'], message: 'code is required for fallback and residue' });
    if (!needsCode && v.code !== undefined) ctx.addIssue({ code: 'custom', path: ['code'], message: 'code is only allowed for fallback and residue' });
  });
export type BenchBmcOverrideRequest = z.infer<typeof benchBmcOverrideRequestSchema>;

export const benchBmcOverrideResponseSchema = z.object({
  /** The stored event id (a bigint as a string); on a replay the id already stored. null for a `cleared` with nothing to clear and no earlier one. */
  eventId: z.string().regex(/^[0-9]{1,19}$/).nullable(),
  reason: benchBmcOverrideReasonSchema,
  replay: z.boolean(),
});
export type BenchBmcOverrideResponse = z.infer<typeof benchBmcOverrideResponseSchema>;

/**
 * SIL-1719 (E-33): the agent's `collect: bmc_log` result for one stage of the ACTIVE linux segment (POST /bench/runs/:id/collected).
 * There is NO device, class, environment, recorder or seq field: the device is the reserved chassis pair, the class is
 * `bmc_reported` and the environment `uefi`, all assigned by the database writer from the pinned profile. `.strict()`.
 */
const benchCollectCount = z.number().int().nonnegative().max(9_999_999);
export const benchCollectedLogSummarySchema = z
  .object({
    entries: benchCollectCount.optional(),
    critical: benchCollectCount.optional(),
    warning: benchCollectCount.optional(),
    windowStartId: z.string().regex(/^[\x20-\x7e]{1,128}$/).optional(),
    windowEndId: z.string().regex(/^[\x20-\x7e]{1,128}$/).optional(),
    /** Why a result is `error` or `skipped`: a closed-shape code, e.g. window_incomplete, log_cleared, no_log_service. */
    reason: z.string().regex(/^[a-z_]{1,40}$/).optional(),
  })
  .strict();
export const benchCollectedStageRequestSchema = z
  .object({
    segmentIdx: bmcSegmentIdx,
    profileId: z.string().min(1).max(256).regex(/^[\x20-\x7e]+$/),
    stageIndex: z.number().int().nonnegative().max(65535),
    status: z.enum(['pass', 'fail', 'error', 'skipped']),
    summary: benchCollectedLogSummarySchema,
  })
  .strict()
  .refine((r) => !((r.status === 'error' || r.status === 'skipped') && r.summary.reason === undefined), {
    message: 'error and skipped need a reason',
    path: ['summary', 'reason'],
  })
  .refine((r) => !(r.status === 'pass' && (r.summary.critical ?? 0) > 0), {
    message: 'a pass carries no critical entries',
    path: ['summary', 'critical'],
  });
export type BenchCollectedStageRequest = z.infer<typeof benchCollectedStageRequestSchema>;

export const benchCollectedStageResponseSchema = z.object({
  seq: z.number().int().nonnegative(),
});
export type BenchCollectedStageResponse = z.infer<typeof benchCollectedStageResponseSchema>;

/** Refusal codes of POST /bench/runs/:id/collected (`error.code`). `collect_disabled` and `stage_conflict` are not failures the agent retries. */
export const BENCH_COLLECTED_REFUSAL_CODES = [
  'collect_disabled', 'stage_conflict', 'collected_cap', 'session_superseded', 'run_not_found', 'run_not_running', 'run_expired',
  'segment_not_active', 'wrong_segment', 'wrong_environment', 'plan_missing', 'stage_not_in_plan', 'stage_not_collected',
  'wrong_channel', 'invalid_body', 'invalid_json', 'rate_limited', 'busy',
] as const;

/** Refusal codes of POST /bench/runs/:id/bmc-events (`error.code` of the standard `{ error: { code, message } }` body). */
export const BENCH_BMC_REFUSAL_CODES = [
  'bmc_not_enabled', 'bmc_pin_mismatch', 'bmc_slot_busy', 'bmc_no_intent', 'bmc_reason_conflict', 'bmc_event_limit',
  'session_superseded', 'run_not_found', 'run_not_running', 'run_expired', 'segment_not_active', 'wrong_segment',
  'wrong_environment', 'invalid_body', 'invalid_json', 'rate_limited', 'busy',
] as const;

/** SIL-2113: refusal codes of POST /bench/runs/:id/bmc-override (`error.code` of the standard `{ error: { code, message } }` body). */
export const BENCH_BMC_OVERRIDE_REFUSAL_CODES = [
  'bmc_not_enabled', 'bmc_no_attempt', 'bmc_override_event_limit',
  'session_superseded', 'run_not_found', 'run_not_running', 'run_expired', 'segment_not_active', 'wrong_segment',
  'wrong_environment', 'invalid_body', 'invalid_json', 'rate_limited', 'busy',
] as const;

/**
 * Read-back for segment B and any restart (resume and attach). Omitted when the run has no BMC event.
 * `openSlots` lists every slot whose record is still open (an intent or a created event with no later removed).
 */
export const benchBmcContinuationSchema = z.object({
  tlsPinSha256: benchBmcHexSchema.optional(),
  openSlots: z
    .array(
      z.object({
        slot: benchBmcSlotSchema,
        /** null only for a `created` record with no intent (the writer cannot produce one; an owner write could): treat the slot as unknown and stop. */
        accountNameSha256: benchBmcHexSchema.nullable(),
        /** True once the created event was recorded (the account certainly existed); false = intent only. */
        created: z.boolean(),
        segmentIdx: bmcSegmentIdx.nullable(),
      }),
    )
    .max(32),
});
export type BenchBmcContinuation = z.infer<typeof benchBmcContinuationSchema>;

/** The agent's read-back of the BMC state at commit (spec 5.7). Required once the run has any BMC intent. */
export const benchBmcAttestationSchema = z
  .object({
    bootOverride: z.enum(['disabled', 'enabled', 'unreadable']),
    virtualMedia: z.enum(['ejected', 'inserted', 'unreadable']),
  })
  .strict();
export type BenchBmcAttestation = z.infer<typeof benchBmcAttestationSchema>;

/**
 * Where a continuing agent picks the run up. Shared by an approved poll that
 * attached this boot to an open run (spec 5.4) and by the resume response.
 */
export const benchRunContinuationSchema = z.object({
  segmentIdx: z
    .number()
    .int()
    .nonnegative()
    .max(MAX_SEGMENTS - 1),
  nextSeq: z.number().int().nonnegative().max(2147483647),
  /**
   * SIL-2023, additive: exactly the seqs the commit treats as stored (graded rows minus operator_attested, includes
   * superseded-segment and bmc_reported rows), ascending. A resumed agent claims these in `stageSeqs`; `nextSeq` is
   * NOT a claim source (stored seqs are gapped by operator rows). Absent from an older API.
   */
  priorSeqs: z.array(z.number().int().nonnegative()).max(MAX_RUN_STAGE_ROWS).optional(),
  /**
   * SIL-1924, additive: true iff EVERY done segment of the run has a claim row (bench_run_segment_claim): each earlier
   * session vouched for its own segment's stage seqs when it handed off, so the server can certify a destructive stage of
   * the continuing segment. Stable from resume/attach to commit (claims are insert-only, done segments are frozen);
   * vacuously true with no done segment. A continuing agent lifts its destructive-stage refusal ONLY when this is
   * exactly true; absent (an older API) or false keeps the refusal, so a drive is never wiped in a session that cannot
   * get a certificate. Absent from an older API.
   */
  segmentClaims: z.boolean().optional(),
  /**
   * SIL-2023, additive: keys whose only listed rows (agent_observed, keyed, segment-tied, non-superseded, evidence
   * registered) have status 'error' and that have NO completed row. Not in completedStageKeys (an agent skips every
   * completed key, so listing 'error' there would stop the stage re-running); lets the agent seed its fatal set.
   * Entries carry no status. Absent from an older API.
   */
  erroredStageKeys: z.array(benchCompletedStageSchema.omit({ status: true })).max(MAX_COMPLETED_STAGES).optional(),
  sessionExpiresAt: z.string().datetime(),
  plan: benchPlanSchema.nullable(),
  completedStageKeys: z.array(benchCompletedStageSchema).max(MAX_COMPLETED_STAGES),
  runState: benchRunStateSchema,
  // E-23a: index of the plan step this segment runs; null when the run has no plan. Additive.
  planStep: z
    .number()
    .int()
    .nonnegative()
    .max(MAX_SEGMENTS - 1)
    .nullable()
    .optional(),
  /** SIL-1714, additive: the BMC pin and open account records (segment B backstop). Omitted when the run has no BMC event. */
  bmc: benchBmcContinuationSchema.optional(),
});

export const benchPairPollResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('authorization_pending') }),
  z.object({ status: z.literal('slow_down') }),
  z.object({ status: z.literal('access_denied') }),
  z.object({ status: z.literal('expired_token') }),
  z.object({
    status: z.literal('approved'),
    sessionToken: z.string(),
    runId: z.string().uuid(),
    wipeCode: z.string().regex(/^\d{6}$/),
    approvedByEmail: z.string(),
    orgName: z.string(),
    attestationLevel: benchAttestationLevelSchema,
    /**
     * SIL-2203, additive: the label printer of the site this run is attested to (`ip:9100`, RFC1918 only), or null/absent
     * when the run has no registered site, the request is not from that site, or the site has no printer. An older agent
     * ignores it; a newer agent re-validates it (printer.rs) and never falls back to the kernel command line in release.
     */
    labelPrinter: z.string().refine(isValidLabelPrinterAddr, 'not a site label printer address').nullable().optional(),
    // Present when the approver attached this boot to an open run instead of
    // starting a new one; `runId` is then that run's id.
    attach: benchRunContinuationSchema.optional(),
  }),
]);

export const benchStageStatusSchema = z.enum(['pass', 'fail', 'timeout', 'error', 'skipped']);

export const benchProfileStageSchema = z
  .object({
    // SIL-2082: non-negative, like the stored stage_index column and benchStageResultSchema (a negative index would
    // never be postable, so a profile declaring one could never be covered).
    stageIndex: z.number().int().min(0),
    name: z.string(),
    executable: z.string(),
    arguments: z.array(z.string()),
    // SIL-2263: STRESS_NG_YAML and MEMTESTER_TEXT are the generic CPU / RAM tests. An agent image older than the one that
    // carries them cannot parse a profile that names them, so such a profile is activated only after the image is out.
    parseFormat: z.enum(['DCGM_JSON', 'NVME_SMART_JSON', 'SMARTCTL_JSON', 'NVME_FORMAT_TEXT', 'STRESS_NG_YAML', 'MEMTESTER_TEXT']),
    fatalOnFailure: z.boolean(),
    destructive: z.boolean(),
    timeoutSeconds: z.number().int().positive(),
    // SIL-1671: absent means `linux` / agent_observed / no collect.
    environment: benchEnvironmentSchema.optional(),
    collect: benchCollectSchema.optional(),
    evidenceClass: benchEvidenceClassSchema.optional(),
    // SIL-2000: absent means required. `false` makes the stage OPTIONAL: an org admin may deselect it per device at
    // pairing approval. Only an owner-published profile version sets it (profile writes are owner-only, SIL-1934).
    required: z.boolean().optional(),
    // SIL-2133 R2: the boot tool this stage runs (uefi + operator-recorded only). The plan step names the tool, never an
    // image; the server resolves it to the pinned image. Old readers strip the unknown field (zod) or ignore it (serde).
    bootTool: benchBootToolIdSchema.optional(),
    // SIL-2246 D1: absent means an untagged (legacy / support) stage, never plan-selected. Legacy matching ignores it, so
    // tagging is expand-only: an old zod reader strips the field, the Rust agent ignores it.
    stepKind: benchStepKindSchema.optional(),
  })
  .superRefine((s, ctx) => {
    const env = s.environment ?? 'linux';
    const cls = s.evidenceClass;
    const bad = (path: string, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    const agentRuns = env === 'linux' || env === 'winpe';
    if (s.destructive && env !== 'linux') bad('environment', 'a destructive stage runs in linux');
    // Stricter than the DB CHECK on purpose: the class follows who observed it
    // (spec 3.3). An in-band BMC read (racadm/ipmitool from Linux) is modelled as
    // `environment: 'bmc'`, not as a linux stage with a BMC class.
    if (agentRuns && cls !== undefined && cls !== 'agent_observed') {
      bad('evidenceClass', `a ${env} stage is agent_observed`);
    }
    if (!agentRuns && (cls === undefined || cls === 'agent_observed')) {
      bad('evidenceClass', `a ${env} stage is bmc_reported or operator_attested`);
    }
    if (env === 'uefi' && s.collect === undefined) bad('collect', 'a uefi stage needs collect');
    if (env !== 'uefi' && s.collect !== undefined) bad('collect', 'collect is only for uefi');
    if (s.bootTool !== undefined && !(env === 'uefi' && s.collect === 'operator')) {
      bad('bootTool', 'bootTool is only for a uefi stage with collect operator');
    }
    if (s.collect === 'operator' && cls !== 'operator_attested') {
      bad('evidenceClass', 'collect operator is operator_attested');
    }
    if (s.collect === 'bmc_log' && cls !== 'bmc_reported') {
      bad('evidenceClass', 'collect bmc_log is bmc_reported');
    }
    // SIL-2000: selection never touches a wipe stage (wipe authorization governs it) and never a stage the agent does
    // not observe (bmc / uefi evidence arrives by another path).
    if (s.required === false) {
      if (s.destructive) bad('required', 'a destructive stage is always required (wipe authorization governs it)');
      if (!agentRuns) bad('required', `a ${env} stage cannot be optional: only agent-observed stages can`);
    }
  });

type StageEnvFields = Pick<
  z.infer<typeof benchProfileStageSchema>,
  'environment' | 'collect' | 'evidenceClass'
>;

/** The environment a stage runs in (absent means linux). */
export function benchStageEnvironment(
  stage: Pick<StageEnvFields, 'environment'>,
): BenchEnvironment {
  return stage.environment ?? 'linux';
}

/**
 * The evidence class of a stage. An absent class is `agent_observed` only where
 * that is legal (linux, winpe); a bmc/uefi stage with no class is invalid and
 * yields null (the schema rejects it).
 */
export function benchStageEvidenceClass(stage: StageEnvFields): BenchEvidenceClass | null {
  if (stage.evidenceClass !== undefined) return stage.evidenceClass;
  const env = benchStageEnvironment(stage);
  return env === 'linux' || env === 'winpe' ? 'agent_observed' : null;
}

/** SIL-2000: a stage is required unless the profile says `required: false`. */
export function benchStageRequired(stage: { required?: boolean }): boolean {
  return stage.required !== false;
}

/**
 * SIL-2000: may an org admin deselect this stage at pairing approval? Optional (`required: false`), NOT destructive
 * (absent counts as not selectable, fail closed) and agent-observed (linux / winpe, evidence class absent or
 * agent_observed). The SQL helper bench_stage_selectable (migration 1138) is the same predicate; the SQL tests pin both
 * against the contract fixtures.
 */
export function benchStageSelectable(
  stage: StageEnvFields & { required?: boolean; destructive?: boolean },
): boolean {
  const env = benchStageEnvironment(stage);
  return (
    !benchStageRequired(stage) &&
    stage.destructive === false &&
    (env === 'linux' || env === 'winpe') &&
    benchStageEvidenceClass(stage) === 'agent_observed'
  );
}

/** A PCI vendor or device id as a profile names it: 4 hex digits, `0x` prefix optional, case-insensitive. */
export const benchPciIdSchema = z.string().regex(/^(0x)?[0-9a-f]{4}$/i);

/**
 * SIL-2133 H1: a chassis vendor/model a profile names. Printable ASCII 1-128 with no leading or trailing space (the agent
 * trims device strings, so an edge space could never match) and no `?` (the agent maps non-ASCII to `?`, so a `?` would
 * match several mangled strings). Mirrors HardwareMatch::violations in proto.rs.
 */
export const benchClassTextSchema = z.string().regex(/^[\x21-\x3e\x40-\x7e](?:[\x20-\x3e\x40-\x7e]{0,126}[\x21-\x3e\x40-\x7e])?$/);

export const benchProfileSchema = z.object({
  id: z.string(),
  version: z.number().int(),
  hardwareMatch: z.object({
    kind: benchDeviceKindSchema,
    // SIL-2082: a 4-digit hex id, optional 0x prefix. An empty or malformed id would silently match nothing (or, once
    // trimmed, everything), so it is a parse failure (the profile then fails closed, see profile-coverage.ts).
    pciVendorId: benchPciIdSchema.optional(),
    pciDeviceId: benchPciIdSchema.optional(),
    // SIL-2133 H1: a chassis profile can name a server class by device vendor/model. Printable ASCII, 1-128, compared
    // exactly (case-insensitive) with device.vendor / device.model. Chassis only; any other kind carrying them fails
    // parse, so the profile fails closed through the existing `broken` path (profile-coverage.ts).
    vendor: benchClassTextSchema.optional(),
    model: benchClassTextSchema.optional(),
  }).refine((m) => m.kind === 'chassis' || (m.vendor === undefined && m.model === undefined), {
    message: 'hardwareMatch vendor/model are allowed only when kind is chassis',
  }),
  executionPolicy: z.object({
    concurrencyGroup: z.string(),
    allowParallelExecution: z.boolean(),
  }),
  stages: z.array(benchProfileStageSchema),
});

/**
 * SIL-2246 R1: one server-resolved device-to-stage assignment of an approved plan. The agent runs exactly `stageIndexes` of
 * profile `profileId`@`version` for the device, in the order given (stageIndex ascending). Opaque to the agent beyond that.
 */
export const benchPlanAssignmentSchema = z.object({
  deviceIndex: z.number().int().nonnegative(),
  deviceKey: asciiText(256).min(1),
  profileId: z.string().min(1).max(128),
  version: z.number().int().positive(),
  stageIndexes: z.array(z.number().int().min(0)).max(256),
});
export type BenchPlanAssignment = z.infer<typeof benchPlanAssignmentSchema>;

/**
 * SIL-2246 R1: GET /bench/profiles. `assignments` (max 256) and `planName` (display only) are present only for a run bound to a
 * plan; an older agent ignores both and runs its built-in rules.
 */
export const benchProfilesResponseSchema = z.object({
  profiles: z.array(benchProfileSchema),
  assignments: z.array(benchPlanAssignmentSchema).max(256).optional(),
  planName: asciiText(128).optional(),
});
export type BenchProfilesResponse = z.infer<typeof benchProfilesResponseSchema>;

// Every column these land in is int4 or text; an out-of-range number or a NUL
// in a string would raise in Postgres and surface as a 500, so the contract
// bounds them (clean 422 instead). `summary` is unbounded JSON; the stages
// route additionally refuses a NUL escape anywhere in it (jsonb rejects it).
const int32 = z.number().int().min(-2147483648).max(2147483647);

/** SIL-2000: most deselections one approval may carry (the DB function refuses more). */
export const MAX_STAGE_DESELECTIONS = 1024;
/** SIL-2000: byte cap of the deselection list as the DB function measures it (jsonb text). */
export const MAX_STAGE_DESELECTIONS_BYTES = 131072;
// The DB function accepts version / stageIndex of at most 9 digits (999999999), so the contract stops there too.
const DESELECTION_INT_MAX = 999_999_999;

/**
 * SIL-2000: one optional stage an org admin removed from one device's requirement at pairing approval. `version` is
 * the profile version the admin saw: a deselection applies only while the served / graded version equals it.
 * Keys are exactly these four (the DB function refuses any other shape).
 */
export const benchStageDeselectionSchema = z
  .object({
    deviceKey: asciiText(256).min(1),
    profileId: asciiText(128).min(1),
    version: int32.min(1).max(DESELECTION_INT_MAX),
    stageIndex: int32.min(0).max(DESELECTION_INT_MAX),
  })
  .strict();
export type BenchStageDeselection = z.infer<typeof benchStageDeselectionSchema>;

/**
 * Upper estimate of the size the DB function measures (octet_length of the jsonb text, which renders ": " and ", "
 * with a space): compact JSON + 7 bytes per entry + ", " between entries + the brackets. Never under-counts, so a list this accepts is
 * never refused for size by the database.
 */
export function benchDeselectionsByteSize(list: readonly BenchStageDeselection[]): number {
  let n = 2 + 2 * Math.max(0, list.length - 1);
  for (const d of list) n += JSON.stringify(d).length + 7;
  return n;
}

export const benchStageResultSchema = z.object({
  // SIL-1683: advisory when stageIndex is present - the API allocates the
  // stored seq and returns it in the ack. Authoritative only for a legacy post
  // (no stageIndex).
  seq: int32.min(0),
  // ProfileStage.stageIndex. With (run, deviceKey, profileId) it is the stage's
  // natural key; its presence selects the idempotent server-seq path.
  stageIndex: int32.min(0).optional(),
  // SIL-1705 (E-19): the run segment the agent believes it is posting into
  // (0 for the paired boot, the attach/resume segment otherwise). ADVISORY:
  // the API stores its own active segment and refuses a mismatch (409
  // wrong_segment). REQUIRED when the run has segment rows (every run approved
  // since E-11); optional only for a legacy run with none. Ignored on an
  // un-keyed (no stageIndex) post. Excluded from the idempotency hash, so a
  // retry of stored bytes is a duplicate whatever segment it names.
  segmentIdx: int32.min(0).max(15).optional(),
  deviceIndex: int32.min(0),
  deviceKey: asciiText(256).min(1),
  profileId: asciiText(128),
  stageName: asciiText(128),
  destructive: z.boolean(),
  fatalOnFailure: z.boolean(),
  wipeTicketId: z.string().uuid().nullable(),
  status: benchStageStatusSchema,
  exitCode: int32.nullable(),
  startedAt: z.string().datetime(),
  finishedAt: z.string().datetime(),
  summary: z.record(z.unknown()),
  outputTruncated: z.boolean(),
  errorReason: asciiText(256).nullable(),
});

export const benchStageAckSchema = z.object({
  seq: z.number().int(),
  result: z.enum(['created', 'duplicate']),
  /**
   * SIL-1924, additive: the segment of the STORED row (null for a legacy NULL-segment row). A duplicate ack can name an
   * earlier segment's row (the replay lookup spans every non-superseded segment); an agent claims only acks whose
   * segmentIdx is its own segment, so it never claims a seq outside its own segment. Absent from an older API.
   * Bounded by the DB column (CHECK segment_idx BETWEEN 0 AND 15), not by MAX_SEGMENTS - 1: this is a stored-row fact the
   * server reports, and a plan-size cap must never make a legitimate ack unparseable.
   */
  segmentIdx: z.number().int().nonnegative().max(15).nullable().optional(),
});

export const benchWipeStartRequestSchema = z.object({ deviceKey: asciiText(256).min(1) });
export const benchWipeStartResponseSchema = z.object({
  wipeTicketId: z.string().uuid(),
  /**
   * SIL-1924, additive: sent by /wipes/ticket (the segment-aware route) only when every earlier segment was vouched for at
   * its handoff and still matches the stored rows (the same predicate as the continuation's `segmentClaims`). A continuing
   * agent runs the destructive tool only on exactly `true`; absent (an older API, rolled back mid-session) means refuse.
   */
  segmentClaims: z.boolean().optional(),
});

// A SHA-256 digest in the S3 checksum header format: 32 bytes, standard base64.
const sha256Base64 = z.string().regex(/^[A-Za-z0-9+/]{43}=$/);

export const benchEvidenceUrlRequestSchema = z.object({
  sha256Base64,
  sizeBytes: z.number().int().positive().max(2_147_483_648),
});
export const benchEvidenceUrlResponseSchema = z.object({
  url: z.string().url(),
  headers: z.record(z.string()),
});

export const benchHandoffRequestSchema = z.object({
  resumeEnvironment: benchAgentEnvironmentSchema,
  foreignVisit: benchForeignVisitSchema.optional(),
  segmentSha256Base64: sha256Base64,
  segmentSize: z.number().int().positive().max(2_147_483_648),
  /**
   * SIL-1924, additive: the seqs the closing agent stored IN ITS OWN SEGMENT (its stage acks with segmentIdx equal to its
   * segment). The server recomputes the segment's agent-claimable seqs under the run lock, in the same transaction as the
   * arm, and refuses (422 stage_mismatch, the arm rolled back) unless they are exactly equal. A handoff without it is a
   * legacy handoff: no claim row, so the run's continuation advertises segmentClaims: false. Entries fit an int4.
   */
  stageSeqs: z.array(z.number().int().nonnegative().max(2147483647)).max(MAX_RUN_STAGE_ROWS).optional(),
});

/**
 * What the PXE/iPXE side may see. No secret, no run id, no resume token: the
 * boot fetch travels the LAN. `bootId` is 128-bit random, single use, and
 * expires with the armed window.
 */
export const benchBootTargetSchema = z.object({
  imageId: asciiText(128).min(1),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  bootId: z.string().regex(/^[A-Za-z0-9_-]{22,64}$/),
  /**
   * SIL-1718 (E-32), additive: the server's per-site agreement to a BMC one-time boot override. `pxe` iff the run's site has
   * bench_site.bmc_enabled on and is not retired; otherwise null. The agent acts only when its own image opt-in
   * (`siliconx.boot_override=pxe`) says the same: either one missing is the manual-PXE fallback. Never a secret.
   */
  override: z.literal('pxe').nullable().optional(),
});

/** The resume secret. Goes only into UEFI NVRAM, never into a boot URL or PXE script. */
export const benchResumeGrantSchema = z.object({
  token: opaqueToken,
  expiresAt: z.string().datetime(),
});

export const benchHandoffResponseSchema = z.object({
  /** Non-null iff the next plan step has a foreignVisit or environment winpe. */
  bootTarget: benchBootTargetSchema.nullable(),
  resume: benchResumeGrantSchema,
});

export const benchResumeRequestSchema = z.object({
  runId: z.string().uuid(),
  resumeToken: opaqueToken,
  environment: benchAgentEnvironmentSchema,
  deviceSummary: z.object({
    chassisSerial: asciiText(128).nullable().optional(),
    boardSerial: asciiText(128).nullable().optional(),
    // SIL-1704 (O1): identity is stored product > chassis > board and matched by set membership; without this field a
    // run identified by its product serial burns its token on a legitimate resume. zod strips unknown keys, so an
    // agent that sends it today would have it silently dropped.
    productSerial: asciiText(128).nullable().optional(),
    primaryMac: z.string().regex(/^([0-9a-f]{2}:){5}[0-9a-f]{2}$/),
    nvmeDeviceKeys: z.array(asciiText(256).min(1)).max(512).optional(),
  }),
  agentVersion: asciiText(64).min(1),
  imageId: asciiText(128).min(1),
});

export const benchResumeResponseSchema = z
  .object({
    sessionToken: opaqueToken,
    wipeCode: z.string().regex(/^\d{6}$/),
    approvedByEmail: z.string(),
    orgName: z.string(),
    attestationLevel: benchAttestationLevelSchema,
    /** SIL-2203, additive. See benchPairPollResponseSchema.labelPrinter. */
    labelPrinter: z.string().refine(isValidLabelPrinterAddr, 'not a site label printer address').nullable().optional(),
  })
  .merge(benchRunContinuationSchema.extend({ plan: benchPlanSchema }));

export const benchNvramStateSchema = z.enum(['absent', 'present', 'unreadable']);
export type BenchNvramState = z.infer<typeof benchNvramStateSchema>;

export const benchCommitRequestSchema = z.object({
  discovery: benchDiscoverySchema,
  stageSeqs: z.array(z.number().int()).max(MAX_RUN_STAGE_ROWS),
  /**
   * SIL-1924, additive: 'segment' = `stageSeqs` is the seqs of the FINAL (active) segment only; the earlier segments are
   * verified from the claims their own agents made at handoff (every done segment must have one, else 409
   * prior_segment_unclaimed). Absent = the legacy rule (the claim equals every non-operator row of the run), plus a
   * fail-closed refusal (422 stage_scope_required) when a continuing segment holds a wipe-ticketed stage.
   */
  stageSeqsScope: z.literal('segment').optional(),
  evidenceSha256Base64: sha256Base64,
  // E-23a: the agent's read-back of the resume-token NVRAM variable. A segmented commit needs 'absent'.
  nvram: z.object({ variable: benchNvramStateSchema }).strict().optional(),
  // SIL-1714 (E-28): the agent's read-back of the BMC boot override and virtual media. Required once the run has any BMC
  // intent (409 bmc_attestation_missing otherwise); must read disabled / ejected (409 bmc_override_not_reset). Recorded
  // in the write-once commit record. Ignored for a run with no BMC intent.
  bmc: benchBmcAttestationSchema.optional(),
});

export const benchLabelSchema = z.object({
  assetRef: z.string(),
  chassisSerial: z.string().nullable(),
  model: z.string().nullable(),
  band: z.string(),
  wipeResult: z.string(),
  attestation: z.enum(['BENCH', 'SELF']),
  runId: z.string().uuid(),
  qrPayload: z.string().url(),
});

export const benchBindingOutcomeSchema = z.enum([
  'matched',
  'created',
  'customs',
  'collision_pending',
  'foreign_refused',
  'refused',
  'component',
  'no_identity',
]);

export const benchCommitResponseSchema = z.object({
  runId: z.string().uuid(),
  label: benchLabelSchema,
  /**
   * SIL-2246 R1, additive: how much of the discovery a bound plan left out. Out-of-scope parts are OMITTED from `devices`
   * (no new BindingOutcome value: the agent's outcome enum is closed), so this is where they are counted. Absent for a run
   * with no plan.
   */
  scope: z.object({ outOfScope: z.number().int().nonnegative(), gaps: z.number().int().nonnegative() }).optional(),
  devices: z.array(
    z.object({
      deviceIndex: z.number().int(),
      outcome: benchBindingOutcomeSchema,
      assetId: z.string().uuid().nullable(),
      appliedBand: z.string().nullable(),
    }),
  ),
});

export type BenchDeviceKind = z.infer<typeof benchDeviceKindSchema>;
export type BenchDevice = z.infer<typeof benchDeviceSchema>;
export type BenchChassis = z.infer<typeof benchChassisSchema>;
export type BenchDiscovery = z.infer<typeof benchDiscoverySchema>;
export type BenchPairStartRequest = z.infer<typeof benchPairStartRequestSchema>;
export type BenchPairStartResponse = z.infer<typeof benchPairStartResponseSchema>;
export type BenchPairPollRequest = z.infer<typeof benchPairPollRequestSchema>;
export type BenchPairPollResponse = z.infer<typeof benchPairPollResponseSchema>;
export type BenchAttestationLevel = z.infer<typeof benchAttestationLevelSchema>;
export type BenchStageStatus = z.infer<typeof benchStageStatusSchema>;
export type BenchProfileStage = z.infer<typeof benchProfileStageSchema>;
export type BenchProfile = z.infer<typeof benchProfileSchema>;
export type BenchStageAck = z.infer<typeof benchStageAckSchema>;
export type BenchStageResult = z.infer<typeof benchStageResultSchema>;
export type BenchRunState = z.infer<typeof benchRunStateSchema>;
export type BenchWipeStartRequest = z.infer<typeof benchWipeStartRequestSchema>;
export type BenchWipeStartResponse = z.infer<typeof benchWipeStartResponseSchema>;
export type BenchEvidenceUrlRequest = z.infer<typeof benchEvidenceUrlRequestSchema>;
export type BenchEvidenceUrlResponse = z.infer<typeof benchEvidenceUrlResponseSchema>;
export type BenchPlanStep = z.infer<typeof benchPlanStepSchema>;
export type BenchPlan = z.infer<typeof benchPlanSchema>;
export type BenchPlanRequest = z.infer<typeof benchPlanRequestSchema>;
export type BenchHandoffRequest = z.infer<typeof benchHandoffRequestSchema>;
export type BenchBootTarget = z.infer<typeof benchBootTargetSchema>;
export type BenchResumeGrant = z.infer<typeof benchResumeGrantSchema>;
export type BenchHandoffResponse = z.infer<typeof benchHandoffResponseSchema>;
export type BenchResumeRequest = z.infer<typeof benchResumeRequestSchema>;
export type BenchCompletedStage = z.infer<typeof benchCompletedStageSchema>;
export type BenchRunContinuation = z.infer<typeof benchRunContinuationSchema>;
export type BenchResumeResponse = z.infer<typeof benchResumeResponseSchema>;
export type BenchCommitRequest = z.infer<typeof benchCommitRequestSchema>;
export type BenchLabel = z.infer<typeof benchLabelSchema>;
export type BenchBindingOutcome = z.infer<typeof benchBindingOutcomeSchema>;
export type BenchCommitResponse = z.infer<typeof benchCommitResponseSchema>;
